import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, UntypedFormGroup, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import { TagService } from '@app/_components/task/tag';

@Component({
    selector: 'app-tag-add-modal',
    templateUrl: './tag-add-modal.component.html',
    styleUrls: ['./tag-add-modal.component.css'],
    standalone: false
})
export class TagAddModalComponent implements OnInit
{
    /**
     * Inspired from https://blog.thoughtram.io/angular/2017/11/13/easy-dialogs-with-angular-material.html
     */

    @Input() tagType: number;
    @Input() tagLabel: string;
    @Input() tagGroupCategories: string[] = [];
    @Output() newTagAddedEvent = new EventEmitter<any>();
    form1: UntypedFormGroup;
    formSubmitted = false;
    formLoading = false;

    visibilities = [
        { value: '1', name: 'Private' },
        { value: '2', name: 'Team' },
        { value: '3', name: 'Public' }
    ];

    // Sentinel value for the "Other" option — triggers the free-text input.
    static readonly OTHER_OPTION: { label: string; value: string } = { label: 'Other', value: 'Other' };

    // Dropdown items derived from `tagGroupCategories` input (categories already in use) + "Other".
    // No hardcoded preset list — the source of truth is the existing DB values.
    mergedCategoryOptions: { label: string; value: string }[] = [];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private dialogRef: MatDialogRef<TagAddModalComponent>,
        private router: Router,
        private tagService: TagService,
        private alertService: AlertService
    ) { }

    validationMessages =
        {
            'name': [
                { type: 'required', message: 'Name is required' }
            ],
            'visibility': [
                { type: 'required', message: 'Visibility is required' }
            ]
        };


    // sourceUrl is used by the api to create the correct link to click to reply to reset email.
    // tslint:disable-next-line:use-lifecycle-interface
    ngOnInit()
    {
        this.mergedCategoryOptions = this.buildMergedCategoryOptions();

        this.form1 = this.formBuilder.group({
            name: ['', Validators.compose([
                Validators.required
            ])],
            visibility: ['2', Validators.compose([
                Validators.required
            ])],
            tagType: [this.tagType, Validators.compose([
                Validators.required
            ])],
            tagGroupCategoryPreset: [null],
            tagGroupCategoryOther: [''],
        }, { validators: [TagAddModalComponent.tagGroupCategoryGroupValidator] });
    }

    private buildMergedCategoryOptions(): { label: string; value: string }[] {
        // Start from categories actually in use (passed in from the parent), then append "Other".
        const inUse = (this.tagGroupCategories || [])
            .filter(c => !!c && c !== TagAddModalComponent.OTHER_OPTION.value)
            .map(c => ({ label: c, value: c }));
        return [...inUse, TagAddModalComponent.OTHER_OPTION];
    }

    isTagGroupCategoryOther(): boolean {
        return this.form1?.get('tagGroupCategoryPreset')?.value === 'Other';
    }

    private static tagGroupCategoryGroupValidator(group: AbstractControl): ValidationErrors | null {
        const preset = group.get('tagGroupCategoryPreset')?.value;
        const other = (group.get('tagGroupCategoryOther')?.value as string)?.trim() ?? '';
        if (preset === 'Other' && !other) {
            return { tagGroupCategoryOtherRequired: true };
        }
        return null;
    }

    private buildTagPayload(): any {
        const v = this.form1.getRawValue();
        const { tagGroupCategoryPreset, tagGroupCategoryOther, ...rest } = v;
        const preset = tagGroupCategoryPreset as string | null;
        const other = (tagGroupCategoryOther as string)?.trim() ?? '';
        const tagGroupCategory =
            preset === 'Other' ? other : (preset ?? null);
        return { ...rest, tagGroupCategory: tagGroupCategory || null };
    }

    onSubmit()
    {
        // stop here if form is invalid
        this.form1.markAllAsTouched();
        if (this.form1.invalid)
        {
            return;
        }

        const payload = this.buildTagPayload();
        this.tagService.add(payload)
            .pipe(first())
            .subscribe(
                data =>
                {
                    console.log('tag-add-modal SUCCESS: ' + JSON.stringify(data));
                    this.newTagAddedEvent.emit(data);
                    this.alertService.success(data, true);
                },
                error =>
                {
                    console.log('error in tag-add-modal: ' + JSON.stringify(error));
                    this.alertService.error(error);
                    this.formLoading = false;
                });


        console.log(this.form1.value.name);
        this.dialogRef.close();

    }
}
